import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Loader2, Download, Printer, ArrowLeft, Check } from "lucide-react";
import { sr } from "../../lib/sr";

export default function ComplianceCertificate() {
  const { planId } = useParams();
  const nav = useNavigate();
  const [cert, setCert] = useState(null);
  const [err, setErr] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    sr.complianceCertificate(planId)
      .then((r) => setCert(r.certificate))
      .catch((e) => setErr(e?.response?.data?.detail || "This plan has no compliance certificate."));
  }, [planId]);

  const download = async () => {
    setDownloading(true);
    try {
      const blob = await sr.downloadComplianceCertificate(planId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `compliance-certificate-${String(planId).slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Certificate downloaded");
    } catch { toast.error("Could not download the PDF"); }
    finally { setDownloading(false); }
  };

  if (err) return (
    <div className="sr-card" data-testid="cert-error">
      <p className="sr-muted">{err}</p>
      <button className="sr-btn sr-btn-ghost sr-btn-sm" style={{ marginTop: 12 }} onClick={() => nav(-1)}><ArrowLeft size={15} /> Back</button>
    </div>
  );
  if (!cert) return <div className="sr-empty"><Loader2 className="sr-spin" /></div>;

  return (
    <div data-testid="compliance-certificate">
      <style>{`@media print { .sr-sidebar, .sr-topbar, .cert-actions, .sr-copilot-fab { display:none !important; } .sr-content { padding:0 !important; } }`}</style>

      <div className="cert-actions" style={{ display: "flex", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => nav(-1)} data-testid="cert-back"><ArrowLeft size={15} /> Back</button>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => window.print()} data-testid="cert-print"><Printer size={15} /> Print</button>
          <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={download} disabled={downloading} data-testid="cert-download">
            {downloading ? <Loader2 size={15} className="sr-spin" /> : <><Download size={15} /> Download PDF</>}
          </button>
        </div>
      </div>

      {/* The certificate sheet */}
      <div className="sr-card" style={{ maxWidth: 760, margin: "0 auto", padding: 40, border: "1px solid #B6E3C6", background: "#fff" }} data-testid="cert-sheet">
        <div style={{ display: "flex", alignItems: "center", gap: 10, color: "#067647", fontWeight: 700, fontSize: 12, letterSpacing: 0.5, textTransform: "uppercase" }}>
          <ShieldCheck size={18} /> SmartReachAI — Advertising Compliance Certificate
        </div>
        <h1 style={{ fontSize: 30, color: "var(--navy)", marginTop: 12 }}>Contextual-Only Marketing Certificate</h1>
        <p style={{ color: "#5b6b85", fontSize: 14, marginTop: 4 }}>{cert.framework}</p>
        <hr style={{ border: "none", borderTop: "1px solid #B6E3C6", margin: "20px 0" }} />

        <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 16 }}>
          <Field label="Issued to" value={cert.business} />
          {cert.goal && <Field label="Campaign goal" value={cert.goal} />}
          <Field label="Issued" value={`${cert.issued_date} · Plan ${String(cert.plan_id).slice(0, 8)} (v${cert.plan_version})`} />
        </div>

        <p style={{ fontSize: 14, color: "#1f2a44", lineHeight: 1.6, marginTop: 20 }}>{cert.summary}</p>

        <div style={{ marginTop: 20 }}>
          <div style={{ fontWeight: 700, color: "var(--navy)", marginBottom: 10 }}>Compliance guardrails enforced</div>
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {(cert.guardrails || []).map((g, i) => (
              <li key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "6px 0", fontSize: 14, color: "#1f2a44" }}>
                <Check size={16} color="#067647" style={{ flexShrink: 0, marginTop: 2 }} /> {g}
              </li>
            ))}
          </ul>
        </div>

        {cert.channels?.length > 0 && (
          <div style={{ marginTop: 18 }}>
            <div style={{ fontWeight: 700, color: "var(--navy)", marginBottom: 8 }}>Approved channels (contextual)</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {cert.channels.map((c, i) => <span key={i} className="sr-badge">{c}</span>)}
            </div>
          </div>
        )}

        <hr style={{ border: "none", borderTop: "1px solid #e2e8f0", margin: "24px 0 14px" }} />
        <p style={{ fontSize: 11.5, color: "#5b6b85", lineHeight: 1.5 }}>
          This certificate attests that the above advertising plan was generated under SmartReachAI&apos;s contextual-only
          guardrails aligned with the Texas Data Privacy and Security Act (TDPSA), the Texas Medical Records Privacy Act
          (TMRPA), and HIPAA-aware advertising standards. It documents the campaign design controls in effect and is not
          legal advice; please review with your compliance counsel.
        </p>
      </div>
    </div>
  );
}

const Field = ({ label, value }) => (
  <div>
    <div style={{ fontSize: 11, color: "#5b6b85" }}>{label}</div>
    <div style={{ fontSize: 15, fontWeight: 700, color: "var(--navy)", marginTop: 2 }}>{value}</div>
  </div>
);
